/**
 * LevNutri — Documento impresso do Plano Alimentar
 * Layout profissional com identidade visual da marca
 */

import React from 'react';
import ReactDOM from 'react-dom';
import { ordenarRefeicoes } from './helpers';
import { Ico, emo } from '../ui/Ico';
import { useMarca, LOGO_LEVNUTRI, reportLogoStyle } from '../../branding';

const PRIMARY   = '#b8895c'; // marrom quente para impressão (funciona melhor em papel)
const SAGE      = '#736b64'; // verde sage
const LIGHT_BG  = '#fbf8f5';
const DARK_TXT  = '#2f2a26';
const MID_TXT   = '#736b64';
const BORDER    = '#d9d0c6';

const MEAL_NAMES = {
  cafe_manha:   'Café da Manhã',
  lanche_manha: 'Lanche da Manhã',
  almoco:       'Almoço',
  lanche_tarde: 'Lanche da Tarde',
  jantar:       'Jantar',
  ceia:         'Ceia',
};

const fmt1 = (n) => (parseFloat(n) || 0).toFixed(1);
const sumArr = (arr, k) => arr.reduce((t, x) => t + (parseFloat(x[k]) || 0), 0);

function calcAge(birthDateStr) {
  if (!birthDateStr) return null;
  const diff = new Date() - new Date(birthDateStr + 'T12:00:00');
  return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
}

function macroPercent(grams, factor, total) {
  if (!total) return 0;
  return Math.round((grams * factor / total) * 100);
}

/* ── Print-only CSS injected as a <style> tag ── */
export function PrintCSS() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');

      @keyframes spin { to { transform: rotate(360deg); } }

      /* ── Screen: hide portal element; it lives outside #root ── */
      @media screen {
        .lev-print-only { display: none !important; }
      }

      /* ── Print: hide the entire React app, show only the portal ── */
      @media print {
        @page {
          size: A4 portrait;
          margin: 1.5cm 2cm;
        }

        /*
         * The portal renders .lev-print-only as a direct <body> child,
         * outside #root. Hiding every body child EXCEPT the portal means
         * no sidebar, no buttons, nothing from the React tree bleeds through.
         * NO position:fixed here — the document flows normally page by page.
         */
        body > *:not(.lev-print-only) { display: none !important; }
        body > .lev-print-only        { display: block !important; }

        /* Page-break helpers */
        .lev-meal-block      { page-break-inside: avoid; break-inside: avoid; }
        .lev-summary-block   { page-break-inside: avoid; break-inside: avoid; }
        .lev-guidelines-block{ page-break-inside: avoid; break-inside: avoid; }
        .lev-footer-block    { page-break-inside: avoid; break-inside: avoid;
                               page-break-before: avoid; break-before: avoid; }
      }
    `}</style>
  );
}

/* ══════════════════════════════════════════════
   Main export
══════════════════════════════════════════════ */
export default function PrintLayout({ plan, patient, user, supplements }) {
  const marca = useMarca();
  if (!plan || !plan.plan_name) return null;
  const supList = (supplements || plan.supplements || []).filter(s => s.nome);

  const meals      = plan.meals || {};
  const mealKeys   = ordenarRefeicoes(meals).map(([k]) => k);
  const age        = calcAge(patient?.birth_date);
  const today      = new Date().toLocaleDateString('pt-BR', { day:'2-digit', month:'long', year:'numeric' });
  const startDate  = plan.start_date
    ? new Date(plan.start_date + 'T12:00:00').toLocaleDateString('pt-BR', { day:'2-digit', month:'long', year:'numeric' })
    : today;

  // Resumo nutricional = SOMA REAL dos alimentos de todas as refeições
  // (fonte única da verdade). Nunca usar os campos armazenados daily_calories/
  // protein_grams etc., que podem divergir da soma das refeições.
  const _macroTotals = mealKeys.reduce((t, k) => {
    const foods = meals[k]?.alimentos || [];
    return {
      cal:  t.cal  + sumArr(foods, 'calorias'),
      prot: t.prot + sumArr(foods, 'proteina'),
      carb: t.carb + sumArr(foods, 'carboidratos'),
      fat:  t.fat  + sumArr(foods, 'gordura'),
    };
  }, { cal:0, prot:0, carb:0, fat:0 });
  const totalKcal = Math.round(_macroTotals.cal);
  const prot      = Math.round(_macroTotals.prot * 10) / 10;
  const carb      = Math.round(_macroTotals.carb * 10) / 10;
  const fat       = Math.round(_macroTotals.fat  * 10) / 10;
  const water     = parseFloat(plan.water_recommendation) || 2;

  const protPct = macroPercent(prot, 4, totalKcal);
  const carbPct = macroPercent(carb, 4, totalKcal);
  const fatPct  = macroPercent(fat,  9, totalKcal);

  const nutriName = user?.name_display || user?.name || 'Nutricionista';
  const crn       = user?.crn || '';

  // Parse guidelines lines
  const guideLines = (plan.guidelines || '')
    .replace(/\\n/g, '\n')
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean);

  const content = (
    <div className="lev-print-only" style={P.wrap}>

      {/* ── CABEÇALHO ── */}
      <header style={P.header}>
        <div style={P.headerLeft}>
          <img
            src={marca.logo || LOGO_LEVNUTRI}
            alt={marca.businessName || 'LevNutri'}
            style={{ ...P.logo, ...reportLogoStyle(marca, 48) }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
        <div style={P.headerRight}>
          <p style={P.nutriName}>{nutriName}</p>
          {crn && <p style={P.nutriCrn}>{crn}</p>}
          <p style={P.headerDate}>Emitido em {today}</p>
        </div>
      </header>
      <div style={P.divider} />

      {/* ── TÍTULO DO PLANO ── */}
      <div style={P.planTitleBlock}>
        <h1 style={P.planTitle}>{plan.plan_name}</h1>
        {plan.service_type && (
          <p style={P.planServiceLabel}>
            {serviceLabel(plan.service_type)}{plan.strategy ? ` · ${strategyLabel(plan.strategy)}` : ''}
          </p>
        )}
      </div>

      {/* ── DADOS DO PACIENTE ── */}
      <section style={P.section}>
        <h2 style={P.sectionTitle}>Dados da Paciente</h2>
        <div style={P.patientGrid}>
          <InfoItem label="Nome" value={patient?.name || '—'} />
          <InfoItem
            label="Data de Nascimento"
            value={patient?.birth_date
              ? `${new Date(patient.birth_date + 'T12:00:00').toLocaleDateString('pt-BR')}${age ? ` (${age} anos)` : ''}`
              : '—'}
          />
          <InfoItem label="Início do Plano" value={startDate} />
          {plan.end_date && (
            <InfoItem
              label="Validade"
              value={new Date(plan.end_date + 'T12:00:00').toLocaleDateString('pt-BR')}
            />
          )}
        </div>
      </section>

      {/* ── RESUMO NUTRICIONAL ── */}
      <section style={{ ...P.section, ...P.summarySection }} className="lev-summary-block">
        <h2 style={{ ...P.sectionTitle, color: '#ffffff' }}>Resumo Nutricional Diário</h2>
        <div style={P.summaryGrid}>
          {/* Calorias em destaque */}
          <div style={P.kcalBlock}>
            <span style={P.kcalNum}>{totalKcal}</span>
            <span style={P.kcalLbl}>kcal / dia</span>
          </div>
          {/* Macros */}
          <div style={P.macrosGrid}>
            <MacroItem label="Proteínas"    grams={prot} pct={protPct} color="#b8c7d8" />
            <MacroItem label="Carboidratos" grams={carb} pct={carbPct} color="#b9d2bd" />
            <MacroItem label="Gorduras"     grams={fat}  pct={fatPct}  color="#e3b3ad" />
            <MacroItem label="Água / dia"   grams={null} extra={`${water}L`} color="#b8c7d8" />
          </div>
        </div>
        {/* Barra de macros */}
        <div style={P.macroBar}>
          <div style={{ width:`${protPct}%`, background:'#7189a6', height:'100%', borderRadius:'3px 0 0 3px' }} />
          <div style={{ width:`${carbPct}%`, background:'#6f9a77', height:'100%' }} />
          <div style={{ width:`${100-protPct-carbPct}%`, background:'#d4a574', height:'100%', borderRadius:'0 3px 3px 0' }} />
        </div>
        <p style={P.macroBarLegend}>
          <span style={{ color:'#7189a6' }}>■ Proteínas {protPct}%</span>
          {'  '}
          <span style={{ color:'#6f9a77' }}>■ Carboidratos {carbPct}%</span>
          {'  '}
          <span style={{ color:'#d4a574' }}>■ Gorduras {fatPct}%</span>
        </p>
      </section>

      {/* ── REFEIÇÕES ── */}
      <section style={P.section}>
        <h2 style={P.sectionTitle}>Refeições</h2>
        {mealKeys.map(key => (
          <MealBlock key={key} mealKey={key} meal={meals[key]} />
        ))}
      </section>

      {/* ── SUPLEMENTAÇÃO ── */}
      {supList.length > 0 && (
        <section style={{ ...P.section }} className="lev-guidelines-block">
          <h2 style={P.sectionTitle}>Suplementação Recomendada</h2>
          <table style={P.supTable}>
            <thead>
              <tr>
                <th style={{ ...P.th, width:'22%' }}>Suplemento</th>
                <th style={{ ...P.th, width:'13%' }}>Dosagem</th>
                <th style={{ ...P.th, width:'20%' }}>Momento de Uso</th>
                <th style={P.th}>Justificativa</th>
              </tr>
            </thead>
            <tbody>
              {supList.map((s, i) => (
                <tr key={i} style={i % 2 === 0 ? P.trEven : P.trOdd}>
                  <td style={{ ...P.td, fontWeight:'600' }}>{emo(s.nome)}</td>
                  <td style={P.td}>{s.dosagem || '—'}</td>
                  <td style={P.td}>{s.momento_uso || '—'}</td>
                  <td style={P.td}>
                    {s.justificativa || '—'}
                    {s.observacao && (
                      <span style={P.supObs}> <Ico e="⚠" /> {s.observacao}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={P.supDisclaimer}>
            * A suplementação deve ser avaliada e prescrita por profissional habilitado. Não iniciar sem orientação médica ou nutricional.
          </p>
        </section>
      )}

      {/* ── ORIENTAÇÕES ── */}
      {guideLines.length > 0 && (
        <section style={{ ...P.section, marginBottom: '1.5rem' }} className="lev-guidelines-block">
          <h2 style={P.sectionTitle}>Orientações Personalizadas</h2>
          <div style={P.guidelinesBox}>
            {guideLines.map((line, i) => (
              <div key={i} style={P.guideItem}>
                <span style={P.guideBullet}>◆</span>
                <span style={P.guideText}>{line}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── RODAPÉ ── */}
      <div className="lev-footer-block" style={{ ...P.footerDivider }}>
      <footer style={P.footer}>
        <p style={P.footerLine1}>
          Plano elaborado por <strong>{nutriName}</strong>{crn ? ` — ${crn}` : ''}
        </p>
        <p style={P.footerLine2}>
          {marca.businessName ? `${marca.businessName} · ` : ''}Gerado com LevNutri
        </p>
        <p style={P.footerLine3}>
          Este plano é personalizado e intransferível. Validade: 30 dias. · Emitido em {today}
        </p>
      </footer>
      </div>
    </div>
  );

  // Portal mounts outside #root, directly in <body>.
  // This lets @media print hide #root without hiding the layout.
  return ReactDOM.createPortal(content, document.body);
}

/* ── Sub-componentes ── */

function InfoItem({ label, value }) {
  return (
    <div style={P.infoItem}>
      <span style={P.infoLabel}>{emo(label)}</span>
      <span style={P.infoValue}>{value}</span>
    </div>
  );
}

function MacroItem({ label, grams, pct, extra, color }) {
  return (
    <div style={{ ...P.macroItem, background: `${color}40`, borderLeft: `3px solid ${color}` }}>
      <p style={P.macroItemVal}>
        {extra || `${fmt1(grams)}g`}
        {pct !== undefined && pct !== null && <span style={P.macroItemPct}> ({pct}%)</span>}
      </p>
      <p style={P.macroItemLbl}>{emo(label)}</p>
    </div>
  );
}

function MealBlock({ mealKey, meal }) {
  const foods = meal.alimentos || [];
  const mCal  = Math.round(sumArr(foods, 'calorias'));
  const mProt = fmt1(sumArr(foods, 'proteina'));
  const mCarb = fmt1(sumArr(foods, 'carboidratos'));
  const mFat  = fmt1(sumArr(foods, 'gordura'));

  return (
    <div style={P.mealBlock} className="lev-meal-block">
      {/* Meal header */}
      <div style={P.mealHeader}>
        <div>
          <span style={P.mealName}>{emo(meal.nome || MEAL_NAMES[mealKey])}</span>
          {meal.horario_sugerido && (
            <span style={P.mealTime}> · {meal.horario_sugerido}</span>
          )}
        </div>
        <div style={P.mealMacros}>
          <span style={P.mealMacroChip}>{mCal} kcal</span>
          <span style={{ ...P.mealMacroChip, color: '#7189a6' }}>{mProt}g prot</span>
          <span style={{ ...P.mealMacroChip, color: '#46714f' }}>{mCarb}g carb</span>
          <span style={{ ...P.mealMacroChip, color: '#c46a60' }}>{mFat}g gord</span>
        </div>
      </div>

      {/* Foods table */}
      {foods.length > 0 && (
        <table style={P.foodTable}>
          <thead>
            <tr>
              <th style={{ ...P.th, width: '38%' }}>Alimento</th>
              <th style={{ ...P.th, width: '18%' }}>Porção</th>
              <th style={P.thNum}>Kcal</th>
              <th style={P.thNum}>Prot. (g)</th>
              <th style={P.thNum}>Carb. (g)</th>
              <th style={P.thNum}>Gord. (g)</th>
            </tr>
          </thead>
          <tbody>
            {foods.map((food, i) => (
              <tr key={i} style={i % 2 === 0 ? P.trEven : P.trOdd}>
                <td style={P.td}>{food.alimento}</td>
                <td style={P.td}>{food.porcao}</td>
                <td style={P.tdNum}>{food.calorias || '—'}</td>
                <td style={P.tdNum}>{fmt1(food.proteina)}</td>
                <td style={P.tdNum}>{fmt1(food.carboidratos)}</td>
                <td style={P.tdNum}>{fmt1(food.gordura)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} style={P.tfootLabel}>Subtotal</td>
              <td style={P.tfootVal}>{mCal}</td>
              <td style={P.tfootVal}>{mProt}</td>
              <td style={P.tfootVal}>{mCarb}</td>
              <td style={P.tfootVal}>{mFat}</td>
            </tr>
          </tfoot>
        </table>
      )}

      {/* Meal observation */}
      {meal.observacoes && (
        <p style={P.mealObs}>
          <em>Obs:</em> {meal.observacoes}
        </p>
      )}
    </div>
  );
}

/* ── helpers ── */
const SERVICE_LABELS = {
  consulta_personalizada:  'Consulta Nutricional Personalizada',
  emagrecimento_feminino:  'Programa de Emagrecimento Feminino',
  equilibrio_hormonal:     'Nutrição para Equilíbrio Hormonal',
  mulheres_ativas:         'Nutrição para Mulheres Ativas',
  reeducacao_compulsao:    'Reeducação Alimentar e Controle da Compulsão',
  planejamento_especializado: 'Planejamento Alimentar Especializado',
  acompanhamento_continuo: 'Acompanhamento Nutricional Contínuo',
};
const STRATEGY_LABELS = {
  low_carb:          'Low Carb',
  cetogenica:        'Cetogênica (Keto)',
  anti_inflamatoria: 'Anti-inflamatória',
  vegetariana:       'Vegetariana',
  vegana:            'Vegana',
};
const serviceLabel  = (id) => SERVICE_LABELS[id]  || id;
const strategyLabel = (id) => STRATEGY_LABELS[id] || id;

/* ══════════════════════════════════════════════
   Estilos — somente para impressão
══════════════════════════════════════════════ */
const P = {
  wrap: {
    fontFamily: "'Nunito', 'Helvetica Neue', Arial, sans-serif",
    fontSize:   '9.5pt',
    color:      DARK_TXT,
    background: '#ffffff',
    padding:    '0',
    lineHeight: 1.5,
  },

  /* Header */
  header: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'flex-start',
    marginBottom:   '12pt',
  },
  headerLeft: { flex: '0 0 auto' },
  headerRight: { textAlign: 'right' },
  logo: { height: '48pt', objectFit: 'contain', display: 'block' },
  nutriName: {
    fontFamily: "'Nunito', sans-serif",
    fontSize:   '13pt',
    fontWeight: '600',
    color:      PRIMARY,
    margin:     '0 0 2pt',
  },
  nutriCrn: {
    fontSize: '8.5pt',
    color:    MID_TXT,
    margin:   '0 0 2pt',
    fontWeight: '600',
  },
  headerDate: {
    fontSize: '8pt',
    color:    MID_TXT,
    margin:   0,
  },
  divider: {
    height:       '2pt',
    background:   `linear-gradient(90deg, ${PRIMARY}, ${SAGE}88)`,
    borderRadius: '1pt',
    marginBottom: '14pt',
  },

  /* Plan title */
  planTitleBlock: { marginBottom: '12pt', textAlign: 'center' },
  planTitle: {
    fontFamily: "'Nunito', sans-serif",
    fontSize:   '17pt',
    fontWeight: '600',
    color:      DARK_TXT,
    margin:     '0 0 4pt',
    lineHeight: 1.3,
  },
  planServiceLabel: {
    fontSize:   '9pt',
    color:      PRIMARY,
    fontWeight: '600',
    margin:     0,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },

  /* Generic section */
  section: {
    marginBottom: '14pt',
  },
  sectionTitle: {
    fontFamily:  "'Nunito', sans-serif",
    fontSize:    '11pt',
    fontWeight:  '600',
    color:       PRIMARY,
    margin:      '0 0 7pt',
    paddingBottom: '4pt',
    borderBottom: `1pt solid ${BORDER}`,
  },

  /* Patient info */
  patientGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap:                 '6pt 16pt',
  },
  infoItem: {
    display:    'flex',
    flexDirection: 'column',
    gap:        '1pt',
  },
  infoLabel: {
    fontSize:   '7.5pt',
    color:      MID_TXT,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  infoValue: {
    fontSize:   '9.5pt',
    color:      DARK_TXT,
    fontWeight: '500',
  },

  /* Summary section */
  summarySection: {
    background:   `linear-gradient(135deg, ${PRIMARY}, #b8895c)`,
    borderRadius: '8pt',
    padding:      '12pt 14pt 10pt',
    color:        '#ffffff',
  },
  summaryGrid: {
    display:    'flex',
    gap:        '16pt',
    alignItems: 'center',
    marginBottom: '8pt',
  },
  kcalBlock: {
    display:     'flex',
    flexDirection: 'column',
    alignItems:  'center',
    minWidth:    '60pt',
  },
  kcalNum: {
    fontFamily: "'Nunito', sans-serif",
    fontSize:   '26pt',
    fontWeight: '600',
    color:      '#ffffff',
    lineHeight: 1,
  },
  kcalLbl: {
    fontSize:   '8pt',
    color:      'rgba(255,255,255,.8)',
    fontWeight: '600',
    letterSpacing: '0.05em',
  },
  macrosGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap:                 '6pt',
    flex:                1,
  },
  macroItem: {
    padding:      '5pt 7pt',
    borderRadius: '4pt',
  },
  macroItemVal: {
    fontSize:   '11pt',
    fontWeight: '600',
    color:      DARK_TXT,
    margin:     '0 0 1pt',
    lineHeight: 1,
  },
  macroItemPct: {
    fontSize:   '8pt',
    fontWeight: '500',
  },
  macroItemLbl: {
    fontSize: '7pt',
    color:    MID_TXT,
    margin:   0,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  macroBar: {
    height:       '6pt',
    borderRadius: '3pt',
    overflow:     'hidden',
    background:   'rgba(255,255,255,.2)',
    display:      'flex',
    marginTop:    '2pt',
    marginBottom: '4pt',
  },
  macroBarLegend: {
    fontSize: '7.5pt',
    color:    'rgba(255,255,255,.85)',
    margin:   0,
    textAlign: 'center',
    letterSpacing: '0.03em',
  },

  /* Meal blocks */
  mealBlock: {
    border:       `1pt solid ${BORDER}`,
    borderRadius: '6pt',
    marginBottom: '8pt',
    overflow:     'hidden',
  },
  mealHeader: {
    display:         'flex',
    justifyContent:  'space-between',
    alignItems:      'center',
    background:      LIGHT_BG,
    padding:         '6pt 10pt',
    borderBottom:    `1pt solid ${BORDER}`,
    flexWrap:        'wrap',
    gap:             '4pt',
  },
  mealName: {
    fontFamily: "'Nunito', sans-serif",
    fontSize:   '10pt',
    fontWeight: '600',
    color:      DARK_TXT,
  },
  mealTime: {
    fontSize:   '8.5pt',
    color:      MID_TXT,
    fontWeight: '500',
  },
  mealMacros: {
    display: 'flex',
    gap:     '5pt',
    flexWrap: 'wrap',
  },
  mealMacroChip: {
    fontSize:     '7.5pt',
    fontWeight:   '600',
    color:        PRIMARY,
    background:   `${PRIMARY}18`,
    padding:      '1pt 5pt',
    borderRadius: '10pt',
    border:       `0.5pt solid ${PRIMARY}40`,
  },

  /* Food table */
  foodTable: {
    width:           '100%',
    borderCollapse:  'collapse',
    fontSize:        '8.5pt',
  },
  th: {
    padding:      '4pt 6pt',
    textAlign:    'left',
    fontWeight:   '600',
    fontSize:     '7pt',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color:        MID_TXT,
    background:   '#f6f0ea',
    borderBottom: `1pt solid ${BORDER}`,
  },
  thNum: {
    padding:    '4pt 6pt',
    textAlign:  'right',
    fontWeight: '600',
    fontSize:   '7pt',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color:      MID_TXT,
    background: '#f6f0ea',
    borderBottom: `1pt solid ${BORDER}`,
  },
  td: {
    padding:     '3.5pt 6pt',
    color:       DARK_TXT,
    borderBottom: `0.5pt solid ${BORDER}`,
    verticalAlign: 'middle',
  },
  tdNum: {
    padding:     '3.5pt 6pt',
    textAlign:   'right',
    color:       DARK_TXT,
    borderBottom: `0.5pt solid ${BORDER}`,
    verticalAlign: 'middle',
  },
  trEven: { background: '#ffffff' },
  trOdd:  { background: '#fbf8f5' },
  tfootLabel: {
    padding:    '4pt 6pt',
    fontWeight: '600',
    fontSize:   '8pt',
    color:      PRIMARY,
    background: '#f6f0ea',
    borderTop:  `1pt solid ${BORDER}`,
  },
  tfootVal: {
    padding:   '4pt 6pt',
    textAlign: 'right',
    fontWeight: '600',
    fontSize:  '8.5pt',
    color:     PRIMARY,
    background: '#f6f0ea',
    borderTop:  `1pt solid ${BORDER}`,
  },
  mealObs: {
    fontSize:  '7.5pt',
    color:     MID_TXT,
    padding:   '5pt 10pt',
    margin:    0,
    background: '#fbf8f5',
    borderTop:  `0.5pt solid ${BORDER}`,
    fontStyle: 'italic',
  },

  /* Supplements table */
  supTable: {
    width:          '100%',
    borderCollapse: 'collapse',
    fontSize:       '8.5pt',
    marginBottom:   '5pt',
  },
  supObs: {
    display:    'block',
    fontSize:   '7pt',
    color:      '#8a5f1a',
    fontStyle:  'italic',
    marginTop:  '1pt',
  },
  supDisclaimer: {
    fontSize:  '7pt',
    color:     MID_TXT,
    fontStyle: 'italic',
    margin:    '3pt 0 0',
  },

  /* Guidelines */
  guidelinesBox: {
    border:       `1pt solid ${BORDER}`,
    borderRadius: '6pt',
    padding:      '8pt 10pt',
    background:   LIGHT_BG,
  },
  guideItem: {
    display:      'flex',
    gap:          '6pt',
    alignItems:   'flex-start',
    marginBottom: '4pt',
  },
  guideBullet: {
    color:      PRIMARY,
    fontSize:   '7pt',
    marginTop:  '2pt',
    flexShrink: 0,
  },
  guideText: {
    fontSize:   '9pt',
    color:      DARK_TXT,
    lineHeight: 1.5,
  },

  /* Footer */
  footerDivider: {
    height:     '1pt',
    background: BORDER,
    margin:     '10pt 0 8pt',
  },
  footer: {
    textAlign: 'center',
  },
  footerLine1: {
    fontSize:   '9pt',
    color:      DARK_TXT,
    margin:     '0 0 2pt',
    fontWeight: '500',
  },
  footerLine2: {
    fontSize: '8.5pt',
    color:    PRIMARY,
    margin:   '0 0 2pt',
    fontWeight: '600',
  },
  footerLine3: {
    fontSize: '7.5pt',
    color:    MID_TXT,
    margin:   0,
  },
};
